import React, { FC, useState, useEffect, useMemo, useContext } from "react";
import { Helmet } from "react-helmet-async";

import Hour from "components/Hour";
import { Fa } from "components/Icon";
import { useTranslation } from "react-i18next";
import { disconnect } from "auth";
import { tw } from "twind";
import { Button, ButtonGroup, Column, Columns, Tooltip } from "@axin-org/comet";
import SelectLang from "components/SelectLang";

interface TemplateProps {
  children: JSX.Element | JSX.Element[];
  renderHeader: React.ReactNode;
}

const DebugContext = React.createContext<{ debug: boolean; toggle(): void }>({
  debug: false,
  toggle: () => {}
});

export const DebugProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [debug, setDebug] = useState(() => {
    const val = sessionStorage.getItem("modeDebug");
    return val ? val === "true" : false;
  });

  useEffect(() => {
    sessionStorage.setItem("modeDebug", debug.toString());
  }, [debug]);

  const providerValue = useMemo(() => {
    return { debug, toggle: () => setDebug(val => !val) };
  }, [debug]);

  return <DebugContext.Provider value={providerValue}>{children}</DebugContext.Provider>;
};

function useModeDebug() {
  return useContext(DebugContext);
}

/**
 * Template des pages pour le suivi de production
 */
const Template: FC<TemplateProps> = props => {
  const [t] = useTranslation();
  const modeDebug = useModeDebug();

  return (
    <div className={tw("bg-gray-50 min-h-screen")}>
      <Helmet defaultTitle="Suivi de production" titleTemplate="Suivi de production - %s" />
      <Columns
        gap={0}
        isMultiline={true}
        className="bg-gray-800 text-white p-2 w-full justify-center items-center"
      >
        <Column width={["full", "full", "7/12"]}>
          <h1 className={tw("font-semibold text-base xl:text-4xl")}>{props.renderHeader}</h1>
        </Column>
        <Column width="narrow">
          <Hour />
        </Column>
        <Column width="3/12" className="justify-end">
          <ButtonGroup>
            <Button
              onClick={modeDebug.toggle}
              intent={modeDebug.debug ? "primary" : "none"}
              title={modeDebug.debug ? "active" : "inactive"}
            >
              <Fa
                icon={["fad", "bug"]}
                className={tw(modeDebug.debug ? "text-white" : "text-blue-600")}
              />
            </Button>
            <SelectLang />
            <Button onClick={() => disconnect()}>
              <Fa icon={["fad", "sign-out"]} className={tw("mr-1 text-blue-600")} />
              <span className={tw("hidden sm:block")}>{t("cbCommun_deconnexion")}</span>
            </Button>
          </ButtonGroup>
        </Column>
      </Columns>

      {props.children}
    </div>
  );
};

export default Template;
